import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import TimeSlotPicker, { TimeSlot } from 'components/TimeSlotPicker';
import clsx from 'clsx';
import { useEffect, useMemo, useRef } from 'react';
import { TIMESPAN } from 'pages/profile/constants';

type Props = {
  advisorName: string;
  timeslots: TimeSlot[];
  onChange: (values: TimeSlot[]) => void;
};

export function SuggestedTimeSlots({ timeslots, advisorName, onChange }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const TOMORROW_DAY = useMemo(() => {
    const tommorow = dayjs().add(1, 'days');
    if (tommorow.day() === 0) {
      // SUNDAY
      return tommorow.add(1, 'days').toDate(); //Switch calendar to start of next monday
    }

    if (tommorow.day() === 6) {
      // SATURDAY
      return tommorow.add(2, 'days').toDate(); //Switch calendar to start of next monday
    }

    return tommorow.toDate();
  }, []);

  const onTimeSlotChange = (value: TimeSlot | undefined, index: number) => {
    const newArr = [...timeslots] as (TimeSlot | undefined)[];
    newArr[index] = value;

    const filteredSlots = newArr.filter((item): item is TimeSlot => !!item);
    onChange(filteredSlots);
  };

  const getPeriodFromDates = (start?: dayjs.Dayjs, end?: dayjs.Dayjs) => {
    if (!start || !end) {
      return TIMESPAN.ALL_DAY;
    }

    const startHours = start.get('hours');
    const endHours = end.get('hours');

    if (startHours === endHours) {
      return TIMESPAN.ALL_DAY;
    }

    if (startHours >= 5 && endHours <= 12) {
      return TIMESPAN.MORNING;
    }

    if (startHours >= 12 && endHours <= 17) {
      return TIMESPAN.AFTERNOON;
    }

    if (startHours >= 17 && endHours <= 23) {
      return TIMESPAN.EVENING;
    }

    return TIMESPAN.ALL_DAY;
  };

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, []);

  return (
    <div ref={ref}>
      <div className={clsx('wisory-title', 'mb-2')}>{t('REACT_BOOKING_FRONTEND.booking2.suggestTimeScreen.title')}</div>
      <div className="text-black mb-6 font-openSans">
        {t('REACT_BOOKING_FRONTEND.booking2.suggestTimeScreen.subtitle', { advisorName })}
      </div>
      <div className="text-white">
        <div className="flex flex-col">
          {timeslots.map((slot, index) => {
            return (
              <div key={index} className="flex flex-col mt-4 sm:flex-row sm:items-center">
                <div className="text-base text-black font-openSans font-bold mb-2 sm:mb-0 sm:w-2/12">
                  {t('customer_info_form_proposal')} {index + 1}
                </div>
                <div className="sm:w-10/12">
                  <TimeSlotPicker
                    minDate={TOMORROW_DAY}
                    selectedDate={slot.startDate}
                    period={getPeriodFromDates(dayjs(slot.startDate), dayjs(slot.endDate))}
                    onChange={(timeslot) => onTimeSlotChange(timeslot, index)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
